import { useMemo, useState } from 'react';
import * as R from 'remeda';
import { Control, Controller, useController, useWatch } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { SchemaSection } from '@rossum/api-client/schemas';
import { ContentWrapper } from '@rossum/components/content-group/ContentWrapper';
import { SplitContent } from '@rossum/components/content-group/SplitContent';
import {
  Autocomplete,
  Button,
  Collapse,
  Divider,
  Stack,
  TextField,
} from '@rossum/ui/material';
import { getSchemaFields } from '../../../formulas/FormulaEditor/helpers';
import { FieldsFormValues } from '../form-model/formModels';

const getContextOptions = (intl: IntlShape) => [
  {
    id: 'self.attr.label',
    label: intl.formatMessage({
      id: 'features.queueSettings.fields.reasoning.meta.label',
    }),
  },
  {
    id: 'self.attr.description',
    label: intl.formatMessage({
      id: 'features.queueSettings.fields.reasoning.meta.description',
    }),
  },
];

export const ReasoningTiles = ({
  control,
  schemaContent,
}: {
  control: Control<FieldsFormValues, undefined>;
  schemaContent: SchemaSection[];
}) => {
  const [advancedVisible, setAdvancedVisible] = useState(false);

  const schemaId = useWatch({ control, name: 'field.id' });

  const { field } = useController({ control, name: 'field.context' });
  const [fields, attributes] = R.partition(field.value, item =>
    item.startsWith('field.')
  );

  const [setFields, setAttributes] = [
    // Purposely shadowing the one I am changing.
    // oxlint-disable-next-line @typescript-eslint/no-shadow
    (fields: string[]) => field.onChange([...fields, ...attributes]),
    // oxlint-disable-next-line @typescript-eslint/no-shadow
    (attributes: string[]) => field.onChange([...fields, ...attributes]),
  ];

  // TODO: Proper escaping with "\" so that python code does not crash.
  const makeField = (id: string) => `field.${id}`;

  const intl = useIntl();

  const schemaFieldOptions = useMemo(
    () =>
      getSchemaFields(schemaContent).filter(
        f => f.category === 'datapoint' && f.id !== schemaId
      ),
    [schemaContent, schemaId]
  );

  const schemaFields = useMemo(
    () =>
      schemaFieldOptions.filter(f => fields.includes(makeField(f.id))) ??
      schemaFieldOptions[0],
    [fields, schemaFieldOptions]
  );

  const contextOptions = getContextOptions(intl);

  return (
    <ContentWrapper>
      <SplitContent
        title={intl.formatMessage({
          id: 'features.queueSettings.fields.form.context.title',
        })}
        description={intl.formatMessage({
          id: 'features.queueSettings.fields.form.context.description',
        })}
      >
        <Stack gap={3}>
          <Autocomplete
            multiple
            size="small"
            options={schemaFieldOptions}
            getOptionLabel={option => `${option.label} (${option.id})`}
            value={schemaFields}
            onChange={(_e, vs) => {
              setFields(vs.map(v => makeField(v.id)));
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={intl.formatMessage({
                  id: 'features.queueSettings.fields.form.relevantFields.placeholder',
                })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            )}
          />
          <Collapse in={advancedVisible}>
            <Stack gap={3}>
              <Divider />
              <Autocomplete
                multiple
                size="small"
                options={contextOptions}
                getOptionLabel={option => `${option.label}`}
                value={contextOptions.filter(op => attributes.includes(op.id))}
                onChange={(_e, vs) => {
                  setAttributes(vs.map(({ id }) => id));
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={intl.formatMessage({
                      id: 'features.queueSettings.fields.reasoning.meta.title',
                    })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                )}
              />
              <Controller
                control={control}
                name="field.prompt"
                render={({ field: promptField }) => (
                  <TextField
                    multiline
                    label={intl.formatMessage({
                      id: 'features.queueSettings.fields.form.additionalInstructions.placeholder',
                    })}
                    helperText={intl.formatMessage({
                      id: 'features.queueSettings.fields.form.additionalInstructions.helperText',
                    })}
                    size="small"
                    {...promptField}
                  />
                )}
              />
            </Stack>
          </Collapse>
        </Stack>
      </SplitContent>
      <Stack spacing={2} pt={3}>
        <Stack direction="row" spacing={2} justifyContent="end" pt={2}>
          <Button
            variant="text"
            color="secondary"
            onClick={() => setAdvancedVisible(v => !v)}
          >
            {advancedVisible
              ? intl.formatMessage({
                  id: 'features.formulas.reasoningTiles.hideOptions',
                })
              : intl.formatMessage({
                  id: 'features.formulas.reasoningTiles.showOptions',
                })}
          </Button>
        </Stack>
      </Stack>
    </ContentWrapper>
  );
};
